import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertOctagon, Check, ChevronLeft, MessageSquare, PauseCircle, PenLine,
  PlayCircle, Send, ShieldAlert,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { AtWillDisclaimer } from "@/components/compass/AtWillDisclaimer";
import { ModuleTitleCOMPASS } from "@/components/compass/ModuleTitleCOMPASS";
import DirectFeedbackHistoryPanel from "@/components/rhythm/DirectFeedbackHistoryPanel";

const ROLE_LEVEL = { employee: 1, manager: 2, po_admin: 3, super_admin: 4 };

export default function CompassPlanDetail() {
  const { planId } = useParams();
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const [settings, setSettings] = useState(null);
  const load = async () => {
    try {
      const [p, s] = await Promise.all([
        api.get(`/compass/plans/${planId}`),
        api.get("/compass/settings"),
      ]);
      setState(p.data);
      setSettings(s.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, [planId]);

  if (!state || !settings) return <p className="italic text-charcoal-muted">Loading plan…</p>;

  const plan = state.plan;
  const level = ROLE_LEVEL[user.role];
  const isEmployee = plan.employee_id === user.id;
  const isManager = plan.manager_id === user.id;
  const isPOPlus = level >= ROLE_LEVEL.po_admin;

  return (
    <div className="space-y-6" data-testid="compass-plan-detail">
      <Link to="/compass" className="patria-btn-ghost -ml-3">
        <ChevronLeft className="h-4 w-4" strokeWidth={1.5} /> COMPASS home
      </Link>

      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <ModuleTitleCOMPASS />
          <h1 className="mt-3 text-4xl font-serif text-forest-900">
            {isEmployee ? "Your Plan" : `Plan · ${plan.employee_name}`}
          </h1>
          <p className="text-sm text-charcoal-muted">
            {plan.gap_category?.replace("_", " ")} · {plan.timeline_days}-day timeline
          </p>
        </div>
        <div className="flex flex-col sm:items-end gap-1">
          <span
            className={
              plan.status === "in_progress" || plan.status === "approved" || plan.status === "delivered" || plan.status === "acknowledged"
                ? "patria-chip-green"
                : plan.status === "paused"
                ? "patria-chip-terra"
                : plan.status === "completed"
                ? "patria-chip-green"
                : "patria-chip-neutral"
            }
          >
            {plan.status.replace("_", " ")}
          </span>
          <p className="text-xs text-charcoal-muted">
            {isEmployee ? "" : `Manager · ${plan.manager_name}`}
          </p>
        </div>
      </div>

      {/* PO controls */}
      {isPOPlus && ["awaiting_po"].includes(plan.status) && (
        <POReviewPanel plan={plan} onDone={load} />
      )}

      {/* Manager actions */}
      {isManager && plan.status === "approved" && (
        <ManagerDeliverPanel planId={plan.id} onDone={load} />
      )}

      {/* Employee acknowledgement (once delivered) */}
      {isEmployee && plan.status === "delivered" && !plan.acknowledged_at && (
        <EmployeeAcknowledgePanel plan={plan} user={user} disclaimer={settings.at_will_disclaimer} onDone={load} />
      )}

      {/* Two-track reminder in the plan header for managers/PO */}
      {(isManager || isPOPlus) && (
        <div className="rounded-2xl border border-terracotta-500/30 bg-terracotta-500/5 p-4 text-sm text-charcoal-soft" data-testid="compass-two-track-reminder">
          <ShieldAlert className="inline h-4 w-4 mr-1 text-terracotta-500" strokeWidth={1.5} />
          COMPASS is for documented work performance or conduct only. Spiritual growth,
          calling, and covenant matters remain on the pastoral track.
        </div>
      )}

      {/* Cross-module: RHYTHM direct-feedback history */}
      {(isManager || isPOPlus) && (
        <DirectFeedbackHistoryPanel employeeId={plan.employee_id} defaultOpen={false} />
      )}

      {/* Pause/resume for PO */}
      {isPOPlus && (
        <PausePanel plan={plan} onDone={load} />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <PanelCard title="The Gap">
          <Field label="Gap description">
            <p className="text-sm whitespace-pre-wrap text-charcoal">{plan.gap_text}</p>
          </Field>
          {plan.gap_evidence && (
            <Field label="Evidence">
              <p className="text-sm whitespace-pre-wrap text-charcoal">{plan.gap_evidence}</p>
            </Field>
          )}
        </PanelCard>
        <PanelCard title="The Standard">
          <Field label="What meeting expectations looks like">
            <p className="text-sm whitespace-pre-wrap text-charcoal">{plan.standard_text}</p>
          </Field>
          <Field label="Standard source">
            <p className="text-sm text-charcoal">{plan.standard_source}</p>
          </Field>
        </PanelCard>
      </div>

      <PanelCard title={`The Measure${plan.measures.length > 1 ? "s" : ""} · ${plan.measures.length}`}>
        <ul className="space-y-3">
          {plan.measures.map((m, i) => (
            <li key={m.id} className="rounded-xl border border-sage-300/60 bg-sage-100/40 p-3">
              <p className="text-xs uppercase tracking-widest text-charcoal-muted">Measure {i + 1}</p>
              <p className="text-sm text-charcoal whitespace-pre-wrap">{m.text}</p>
            </li>
          ))}
        </ul>
      </PanelCard>

      {plan.resources_and_support && (
        <PanelCard title="Resources & support committed">
          <p className="text-sm whitespace-pre-wrap text-charcoal">{plan.resources_and_support}</p>
        </PanelCard>
      )}

      <AtWillDisclaimer text={settings.at_will_disclaimer} />

      {plan.acknowledged_at && (
        <div className="patria-card p-5" data-testid="compass-ack-block">
          <p className="patria-chip-green">Acknowledged</p>
          <p className="mt-2 text-sm text-charcoal">
            Signed by <strong>{plan.acknowledgment_signature}</strong> on {plan.acknowledged_at}
          </p>
          {plan.acknowledgment_comment && (
            <p className="mt-1 text-sm text-charcoal-soft italic">&ldquo;{plan.acknowledgment_comment}&rdquo;</p>
          )}
        </div>
      )}

      {/* Check-ins */}
      <PanelCard title={`Check-ins · ${state.check_ins.length}`}>
        <ul className="space-y-3" data-testid="compass-checkins">
          {state.check_ins.map((c) => (
            <CheckinCard
              key={c.id}
              c={c}
              plan={plan}
              user={user}
              isManager={isManager}
              isPOPlus={isPOPlus}
              isEmployee={isEmployee}
              onDone={load}
            />
          ))}
        </ul>
      </PanelCard>

      {/* Written responses (employee only entry point; everyone can view) */}
      <WrittenResponses plan={plan} state={state} user={user} isEmployee={isEmployee} onDone={load} />

      {/* Decision */}
      {(isManager || isPOPlus) && ["in_progress", "delivered", "acknowledged"].includes(plan.status) && (
        <DecisionPanel plan={plan} isPOPlus={isPOPlus} onDone={load} />
      )}
      {plan.outcome && (
        <PanelCard title="Outcome" testId="compass-outcome">
          <p className="patria-chip-green capitalize">{plan.outcome.decision.replace("_", " ")}</p>
          {plan.outcome.subtype && <p className="mt-1 text-sm text-charcoal-muted">Subtype: {plan.outcome.subtype}</p>}
          <p className="mt-2 text-sm text-charcoal whitespace-pre-wrap">{plan.outcome.notes}</p>
          {plan.outcome.leadership_signoff && (
            <p className="mt-2 text-xs text-charcoal-muted italic">Leadership sign-off: {plan.outcome.leadership_signoff}</p>
          )}
          {plan.outcome.extension_rationale && (
            <div className="mt-3 rounded-lg bg-sage-100 border border-sage-300/60 p-3 text-sm">
              <p className="font-medium text-forest-900">Extension Rationale</p>
              <p>{plan.outcome.extension_rationale}</p>
              <p className="text-xs text-charcoal-muted mt-1">
                {plan.outcome.extension_days} additional days · new check-in dates: {plan.outcome.extension_added_dates?.join(", ")}
              </p>
            </div>
          )}
        </PanelCard>
      )}
    </div>
  );
}

/* ------ Sub-components ------ */

function PanelCard({ title, children, testId }) {
  return (
    <section className="patria-card p-6" data-testid={testId}>
      <h3 className="text-xl font-serif text-forest-900 mb-3">{title}</h3>
      {children}
    </section>
  );
}
function Field({ label, children }) {
  return (
    <div className="mb-3">
      <p className="text-xs uppercase tracking-widest text-charcoal-muted">{label}</p>
      {children}
    </div>
  );
}

function POReviewPanel({ plan, onDone }) {
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const act = async (action) => {
    if (action === "return" && !notes.trim()) {
      toast.error("Please add notes so the manager can address them.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/compass/plans/${plan.id}/po-review`, { action, po_notes: notes });
      toast.success(action === "approve" ? "Plan approved." : "Plan returned to manager.");
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="patria-card p-5 border-l-4 border-terracotta-500" data-testid="po-plan-review">
      <p className="patria-chip-terra">People Operations</p>
      <h3 className="text-xl font-serif text-forest-900 mt-2">Review This Plan</h3>
      <textarea
        className="patria-input w-full min-h-[90px] mt-3"
        placeholder="Notes (required to return)"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
      />
      <div className="mt-3 flex gap-2 justify-end">
        <button className="patria-btn-secondary" disabled={busy} onClick={() => act("return")} data-testid="po-plan-return">
          Return with notes
        </button>
        <button className="patria-btn-primary" disabled={busy} onClick={() => act("approve")} data-testid="po-plan-approve">
          <Check className="h-4 w-4" strokeWidth={1.5} /> Approve plan
        </button>
      </div>
    </div>
  );
}

function ManagerDeliverPanel({ planId, onDone }) {
  const [busy, setBusy] = useState(false);
  const deliver = async () => {
    if (!window.confirm("Mark the delivery meeting complete? The employee will be notified.")) return;
    setBusy(true);
    try {
      await api.post(`/compass/plans/${planId}/deliver`, { delivery_notes: "" });
      toast.success("Plan delivered. Employee has been notified.");
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally { setBusy(false); }
  };
  return (
    <div className="patria-card p-5" data-testid="mgr-deliver-panel">
      <p className="text-sm text-charcoal-soft">
        Once you&rsquo;ve met with the employee and walked through the plan, mark it as delivered.
        The employee will be prompted to acknowledge in MyPatria.
      </p>
      <button className="patria-btn-primary mt-3" disabled={busy} onClick={deliver} data-testid="mgr-deliver-btn">
        <Send className="h-4 w-4" strokeWidth={1.5} /> Mark delivery complete
      </button>
    </div>
  );
}

function EmployeeAcknowledgePanel({ plan, user, disclaimer, onDone }) {
  const [typed, setTyped] = useState("");
  const [consent, setConsent] = useState(false);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/compass/plans/${plan.id}/acknowledge`, {
        typed_name: typed, consent, comment,
      });
      toast.success("Acknowledgment recorded — thank you.");
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally { setBusy(false); }
  };
  return (
    <div className="patria-card p-6" data-testid="employee-ack-panel">
      <h3 className="text-xl font-serif text-forest-900">Acknowledge Your Plan</h3>
      <p className="mt-2 text-sm text-charcoal-soft">
        Acknowledging records receipt — not agreement. You may attach a written response any time.
      </p>
      <div className="mt-4 rounded-2xl border-l-4 border-terracotta-500 bg-cream-100 p-4 text-sm text-charcoal-soft whitespace-pre-wrap">
        {disclaimer}
      </div>
      <div className="mt-4 space-y-3">
        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="mt-1"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            data-testid="ack-consent"
          />
          <span>I confirm I have reviewed this plan with my manager.</span>
        </label>
        <div>
          <label className="block text-xs uppercase tracking-widest font-medium text-charcoal-soft mb-1">
            Type your full name exactly as it appears on your profile ({user.name})
          </label>
          <input className="patria-input w-full sm:max-w-md" value={typed} onChange={(e) => setTyped(e.target.value)} data-testid="ack-typed-name" />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-widest font-medium text-charcoal-soft mb-1">
            Optional comment (preserved permanently)
          </label>
          <textarea className="patria-input w-full min-h-[80px]" value={comment} onChange={(e) => setComment(e.target.value)} data-testid="ack-comment" />
        </div>
      </div>
      <button className="patria-btn-primary mt-4" onClick={submit} disabled={busy || !consent || !typed.trim()} data-testid="ack-submit">
        <PenLine className="h-4 w-4" strokeWidth={1.5} /> Sign & acknowledge
      </button>
    </div>
  );
}

function PausePanel({ plan, onDone }) {
  const [showPause, setShowPause] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const canPause = ["in_progress", "delivered"].includes(plan.status);
  const isPaused = plan.status === "paused";

  const pause = async () => {
    if (!reason.trim()) {
      toast.error("Please note the pause reason.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/compass/plans/${plan.id}/pause`, { reason });
      toast.success("Plan paused. Check-ins are frozen; timeline will shift on resume.");
      setShowPause(false);
      setReason("");
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally { setBusy(false); }
  };
  const resume = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/compass/plans/${plan.id}/resume`, {});
      toast.success(`Plan resumed. Check-in dates shifted by ${data.shifted_days} day(s).`);
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (!canPause && !isPaused) return null;
  return (
    <div className="patria-card p-4 flex items-center gap-3" data-testid="po-pause-panel">
      <AlertOctagon className="h-4 w-4 text-terracotta-500" strokeWidth={1.5} />
      <div className="flex-1 text-sm text-charcoal-soft">
        {isPaused
          ? "This plan is currently PAUSED — check-ins are frozen. Leave never counts against the employee."
          : "Pause the plan for protected leave. Check-ins freeze; timeline shifts on resume."}
      </div>
      {isPaused ? (
        <button className="patria-btn-primary" onClick={resume} disabled={busy} data-testid="po-resume-btn">
          <PlayCircle className="h-4 w-4" strokeWidth={1.5} /> Resume
        </button>
      ) : (
        <button className="patria-btn-secondary" onClick={() => setShowPause(true)} data-testid="po-pause-btn">
          <PauseCircle className="h-4 w-4" strokeWidth={1.5} /> Pause
        </button>
      )}
      {showPause && (
        <div className="fixed inset-0 z-50 bg-forest-900/50 flex items-center justify-center p-4">
          <div className="patria-card w-full max-w-md p-6">
            <h3 className="text-2xl font-serif text-forest-900">Pause This Plan</h3>
            <p className="mt-2 text-sm text-charcoal-soft">Reason (leave category or brief note). Not shown as a gap.</p>
            <textarea className="patria-input w-full min-h-[90px] mt-3" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="po-pause-reason" />
            <div className="mt-4 flex justify-end gap-3">
              <button className="patria-btn-secondary" onClick={() => setShowPause(false)}>Cancel</button>
              <button className="patria-btn-primary" onClick={pause} disabled={busy} data-testid="po-pause-confirm">Pause</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CheckinCard({ c, plan, user, isManager, isPOPlus, isEmployee, onDone }) {
  const [expanded, setExpanded] = useState(c.status !== "completed");
  const [results, setResults] = useState({});
  const [notes, setNotes] = useState("");
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const canDocument = (isManager || isPOPlus) && (c.status === "scheduled" || c.status === "missed") && plan.status !== "paused";
  const canComment = isEmployee || isManager || isPOPlus;

  const setResult = (mid, patch) => setResults((r) => ({ ...r, [mid]: { ...(r[mid] || {}), ...patch } }));

  const submit = async () => {
    const measure_results = plan.measures.map((m) => {
      const r = results[m.id] || {};
      return { measure_id: m.id, result: r.result || "", notes: r.notes || "" };
    });
    if (measure_results.some((mr) => !mr.result)) {
      toast.error("Please address every measure (met / not met / support needed).");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/compass/plans/${plan.id}/checkins/${c.id}/document`, {
        measure_results, notes,
      });
      toast.success("Check-in documented.");
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const submitComment = async () => {
    if (!comment.trim()) return;
    try {
      await api.post(`/compass/plans/${plan.id}/checkins/${c.id}/comment`, { text: comment });
      setComment("");
      toast.success("Comment posted.");
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  return (
    <li className="rounded-2xl border border-sage-300/60 bg-cream-100 p-4" data-testid={`checkin-${c.id}`}>
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="font-medium text-forest-900">{c.scheduled_date}</p>
          <span className={
            c.status === "completed" ? "patria-chip-green" :
            c.status === "missed" ? "patria-chip-terra" : "patria-chip-neutral"
          }>{c.status}</span>
        </div>
        <button className="patria-btn-ghost text-xs" onClick={() => setExpanded((e) => !e)}>
          {expanded ? "Collapse" : "Expand"}
        </button>
      </div>

      {expanded && (
        <div className="mt-3 space-y-3">
          {c.status === "completed" ? (
            <>
              {c.notes && <p className="text-sm text-charcoal-soft whitespace-pre-wrap">{c.notes}</p>}
              <ul className="text-sm space-y-1">
                {(c.measure_results || []).map((mr) => {
                  const m = plan.measures.find((mm) => mm.id === mr.measure_id);
                  return (
                    <li key={mr.measure_id} className="flex items-start gap-2">
                      <span className={
                        mr.result === "met" ? "patria-chip-green" :
                        mr.result === "not_met" ? "patria-chip-terra" : "patria-chip-neutral"
                      }>{mr.result.replace("_", " ")}</span>
                      <span className="text-charcoal">{(m?.text || "").slice(0, 90)}{(m?.text || "").length > 90 ? "…" : ""}</span>
                    </li>
                  );
                })}
              </ul>
            </>
          ) : canDocument ? (
            <div className="space-y-3">
              {plan.measures.map((m) => (
                <div key={m.id} className="rounded-lg bg-sage-100 border border-sage-300/60 p-3">
                  <p className="text-sm text-charcoal">{m.text}</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {["met", "not_met", "support_needed"].map((r) => (
                      <button
                        key={r}
                        className={`rounded-full px-3 py-1 text-xs font-medium border ${
                          results[m.id]?.result === r
                            ? "bg-forest-600 text-cream-100 border-forest-600"
                            : "bg-cream-100 text-charcoal border-sage-300"
                        }`}
                        onClick={() => setResult(m.id, { result: r })}
                        data-testid={`checkin-${c.id}-measure-${m.id}-${r}`}
                      >
                        {r.replace("_", " ")}
                      </button>
                    ))}
                  </div>
                  <input
                    className="patria-input w-full text-xs mt-2"
                    placeholder="Note (optional)"
                    value={results[m.id]?.notes || ""}
                    onChange={(e) => setResult(m.id, { notes: e.target.value })}
                  />
                </div>
              ))}
              <textarea
                className="patria-input w-full text-sm"
                placeholder="Overall notes (optional)"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                data-testid={`checkin-${c.id}-notes`}
              />
              <div className="flex justify-end">
                <button className="patria-btn-primary" onClick={submit} disabled={busy} data-testid={`checkin-${c.id}-submit`}>
                  Document check-in
                </button>
              </div>
            </div>
          ) : (
            <p className="italic text-charcoal-muted text-sm">
              {plan.status === "paused" ? "Plan is paused — check-ins are frozen." : "Waiting for check-in."}
            </p>
          )}

          {c.employee_comments?.length > 0 && (
            <div>
              <p className="text-xs uppercase tracking-widest text-charcoal-muted mb-1">Comments</p>
              <ul className="space-y-1">
                {c.employee_comments.map((cm, i) => (
                  <li key={`${cm.at}-${cm.by_user_name}-${i}`} className="text-sm text-charcoal">
                    <span className="font-medium">{cm.by_user_name}</span>
                    <span className="text-xs text-charcoal-muted ml-2">{cm.at}</span>
                    <p className="whitespace-pre-wrap">{cm.text}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {canComment && (
            <div className="flex gap-2">
              <input
                className="patria-input flex-1 text-sm"
                placeholder="Add a comment…"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                data-testid={`checkin-${c.id}-comment-input`}
              />
              <button className="patria-btn-secondary" onClick={submitComment} data-testid={`checkin-${c.id}-comment-submit`}>
                <MessageSquare className="h-4 w-4" strokeWidth={1.5} />
              </button>
            </div>
          )}
        </div>
      )}
    </li>
  );
}

function WrittenResponses({ plan, state, user, isEmployee, onDone }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post(`/compass/plans/${plan.id}/written-responses`, { text });
      setText("");
      toast.success("Response saved.");
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally { setBusy(false); }
  };
  return (
    <PanelCard title={`Employee written responses · ${state.written_responses.length}`} testId="compass-written-responses">
      <p className="text-xs text-charcoal-muted italic">
        Responses are preserved permanently — they may be edited (which creates a new version) but never deleted.
      </p>
      <ul className="mt-3 space-y-3">
        {state.written_responses.map((r) => (
          <li key={r.id} className="rounded-xl border border-sage-300/60 bg-cream-100 p-4 text-sm">
            <p className="whitespace-pre-wrap text-charcoal">{r.text}</p>
            <p className="text-xs text-charcoal-muted mt-1">
              Last updated {r.updated_at}
              {r.versions.length > 0 && ` · ${r.versions.length} previous revision${r.versions.length === 1 ? "" : "s"}`}
            </p>
          </li>
        ))}
      </ul>
      {isEmployee && (
        <div className="mt-4">
          <textarea
            className="patria-input w-full min-h-[100px]"
            placeholder="Add a written response…"
            value={text}
            onChange={(e) => setText(e.target.value)}
            data-testid="written-response-input"
          />
          <div className="mt-2 flex justify-end">
            <button className="patria-btn-primary" onClick={submit} disabled={busy || !text.trim()} data-testid="written-response-submit">
              Save response
            </button>
          </div>
        </div>
      )}
    </PanelCard>
  );
}

function DecisionPanel({ plan, isPOPlus, onDone }) {
  const [decision, setDecision] = useState("");
  const [subtype, setSubtype] = useState("");
  const [notes, setNotes] = useState("");
  const [leadership, setLeadership] = useState("");
  const [extRationale, setExtRationale] = useState("");
  const [extDays, setExtDays] = useState(30);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!decision) return toast.error("Choose an outcome.");
    if (!notes.trim()) return toast.error("Please write outcome notes.");
    if (decision === "extension" && (!isPOPlus || !extRationale.trim())) {
      return toast.error(!isPOPlus ? "Extensions require People Operations." : "Extension rationale is required.");
    }
    setBusy(true);
    try {
      await api.post(`/compass/plans/${plan.id}/decision`, {
        decision, subtype, notes, leadership_signoff: leadership,
        extension_rationale: decision === "extension" ? extRationale : undefined,
        extension_days: decision === "extension" ? extDays : undefined,
      });
      toast.success("Decision recorded.");
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally { setBusy(false); }
  };
  return (
    <PanelCard title="Record outcome" testId="compass-decision-panel">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {[
          { k: "met", label: "Met — restored & recognized", cls: "bg-forest-600 text-cream-100" },
          { k: "not_met", label: "Not met", cls: "bg-terracotta-500 text-cream-100" },
          { k: "extension", label: "Extension (PO only)", cls: "bg-sage-200 text-charcoal" },
        ].map((o) => (
          <button
            key={o.k}
            className={`rounded-2xl border px-4 py-3 text-sm font-medium ${
              decision === o.k ? o.cls : "bg-cream-100 border-sage-300 text-charcoal"
            } ${o.k === "extension" && !isPOPlus ? "opacity-60 cursor-not-allowed" : ""}`}
            onClick={() => (o.k === "extension" && !isPOPlus) ? null : setDecision(o.k)}
            data-testid={`decision-${o.k}`}
          >
            {o.label}
          </button>
        ))}
      </div>
      {decision === "not_met" && (
        <input
          className="patria-input w-full mt-3"
          placeholder="Subtype (e.g., 'reassignment where possible', 'respectful separation')"
          value={subtype}
          onChange={(e) => setSubtype(e.target.value)}
          data-testid="decision-subtype"
        />
      )}
      {decision === "extension" && (
        <div className="mt-3 space-y-3 rounded-2xl border border-sage-300/60 bg-sage-100/40 p-4">
          <p className="text-xs uppercase tracking-widest text-charcoal-muted">Extension Length</p>
          <div className="flex gap-2 flex-wrap">
            {[30, 60, 90].map((n) => (
              <button
                key={n}
                className={`rounded-full px-3 py-1 text-sm ${extDays === n ? "bg-forest-600 text-cream-100" : "bg-cream-100 border border-sage-300"}`}
                onClick={() => setExtDays(n)}
              >
                {n} days
              </button>
            ))}
          </div>
          <textarea
            className="patria-input w-full min-h-[90px]"
            placeholder="Documented rationale for extension"
            value={extRationale}
            onChange={(e) => setExtRationale(e.target.value)}
            data-testid="decision-extension-rationale"
          />
        </div>
      )}
      <textarea
        className="patria-input w-full min-h-[100px] mt-3"
        placeholder="Outcome notes"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        data-testid="decision-notes"
      />
      <input
        className="patria-input w-full mt-3"
        placeholder="Leadership sign-off (name/title/date)"
        value={leadership}
        onChange={(e) => setLeadership(e.target.value)}
        data-testid="decision-leadership"
      />
      <div className="mt-4 flex justify-end">
        <button className="patria-btn-primary" onClick={submit} disabled={busy} data-testid="decision-submit">
          Record outcome
        </button>
      </div>
    </PanelCard>
  );
}
